import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useHierarchy } from 'hooks';
import { Modal } from 'semantic-ui-react';
import { RecordOf } from 'immutable';
import mixpanel from 'mixpanel-browser';

// COMPONENTS
import ApiErrors from 'components/ApiErrors';
import { PrimaryButton, SuccessButton } from 'components/Buttons';
import {
  StyledModal,
  StyledModalHeader,
  StyledModalContent,
  StyledDescription,
  StyledModalActions,
  StyledCancelButton,
} from '../styles';
import DealershipModalCard from './components/DealershipModalCard.component';
import VehicleModalCard from './components/VehicleModalCard.component';
import RentalDetailsModalCard from './components/RentalDetailsModalCard.component';
import CustomerModalCard, { emptyCustomer } from './components/CustomerModalCard.component';
import FlashMessage from 'components/FlashMessage';

// CONSTANTS
import { AgreementStatus, BASE_DEFAULT_DAYS_OUT } from 'constants/agreements';
import { FLASH_MESSAGE_TYPES } from 'constants/common';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

// SELECTORS
import { selectFleetSettings } from 'services/FleetSettings/selectors';
import { selectUser } from 'services/Authentication/selectors';
import { selectDealershipSettings } from 'services/DealershipSettings/selectors';

// THUNKS
import { createAgreement } from 'services/Agreements/thunks';

// UTILS
import { useAppDispatch } from 'hooks/store';
import { Agreement, Customer, Vehicle } from 'services/resources';
import { historyPush } from 'utils/historyPush.utils';
import { mapDealershipHierarchyForDropdowns } from 'utils/dealershipHierarchy.utils';

interface ButtonOptions {
  redirect: boolean;
  status: AgreementStatus;
}

interface Props {
  description: string;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  onPrimaryCallback: (id?: number) => void;
  onSuccessCallback: (id?: number) => void;
  preselectedCustomer?: Customer;
  customerReadOnly: boolean;
  preselectedDateOut?: Date;
  preselectedDateIn?: Date;
  primaryButtonClickOptions: ButtonOptions;
  primaryButtonLabel: string;
  successButtonClickOptions: ButtonOptions;
  successButtonLabel: string;
  vehicle: RecordOf<Vehicle> | undefined;
}

type State = {
  customer: Customer | typeof emptyCustomer;
  customerReadOnly: boolean;
  dateOut: Date | null;
  dateDue: Date | null;
  errors: {
    phoneCell: string | { id: string } | null;
    createAgreement: string | JSX.Element | JSX.Element[] | null;
    dateDue: string | { id: string } | null;
  };
};

// TODO: refactor to use Formik
const AgreementModal = ({
  description,
  isOpen,
  onClose,
  onOpen,
  onPrimaryCallback,
  onSuccessCallback,
  preselectedCustomer,
  customerReadOnly,
  preselectedDateOut,
  preselectedDateIn,
  primaryButtonClickOptions,
  primaryButtonLabel,
  successButtonClickOptions,
  successButtonLabel,
  vehicle,
}: Props) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const dealershipSettings = useSelector(selectDealershipSettings());
  const fleetSettings = useSelector(selectFleetSettings());
  const user = useSelector(selectUser());

  const [creationLoading, setCreationLoading] = useState(false);

  const { formatMessage } = useIntl();

  const defaultState = {
    customer: preselectedCustomer || emptyCustomer,
    customerReadOnly: customerReadOnly ?? false,
    dateOut: preselectedDateOut,
    dateDue: preselectedDateIn,
    errors: {
      phoneCell: null,
      createAgreement: null,
      dateDue: null,
    },
  };

  const defaultDaysOut = fleetSettings.get('defaultDayOut') ?? BASE_DEFAULT_DAYS_OUT;

  const [agreementModalState, setAgreementModalState] = useState<State>(defaultState);
  const [isFlashMessageVisible, setIsFlashMessageVisible] = useState(false);
  const { customer, dateDue, dateOut, errors } = agreementModalState;

  const { hierarchy, currentlySelectedEntity, selectHierarchyEntity, hasChildren } = useHierarchy();

  // replace the "self" dealership with the actual id for DealershipModalCard dropdown
  const mappedHierarchy = mapDealershipHierarchyForDropdowns(hierarchy, user);

  // we want to display a dealership selector in the case that the user is at "All Entities" scope, and no vehicle nor customer belonging to a dealership are available at modal load
  const [displayDealershipSelector, setDisplayDealershipSelector] = useState<boolean>(false);
  useEffect(() => {
    const isAllEntitiesScope = hasChildren && currentlySelectedEntity === 'hierarchy';
    if (vehicle === undefined && !('dealership' in customer) && hasChildren && isAllEntitiesScope) {
      setDisplayDealershipSelector(true);
    }
  }, []);

  const [selectedDealership, setSelectedDealership] = useState<number | null>(null);

  const errorMessage = agreementModalState.errors.createAgreement;

  const displayedDateOut = dateOut || new Date();
  const displayedDateDue = dateDue ? new Date(dateDue) : new Date(displayedDateOut);
  if (!dateDue) {
    displayedDateDue.setDate(displayedDateOut.getDate() + defaultDaysOut);
  }

  const anyError = Object.values(errors).some((value) => Boolean(value));

  const requiredFields = customer.firstName && customer.lastName && customer.phoneCell;

  const setCustomer = (newCustomer: Customer) => {
    setAgreementModalState((prev) => {
      return { ...prev, customer: newCustomer };
    });
  };

  const setErrors = (error) => {
    setAgreementModalState((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        ...error,
      },
    }));
  };

  const setDateOut = (newDateOut: Date) => {
    setAgreementModalState((prev) => {
      let dateDueError: { id: string };

      if (prev.dateDue && newDateOut > prev.dateDue) {
        // If setting dateOut later than dateDue
        dateDueError = { id: 'errors.validation.dateDueBeforeDateOut' };
      } else if (prev.dateDue && newDateOut < prev.dateDue) {
        // If setting dateOut before dateDue
        dateDueError = null;
      }

      return {
        ...prev,
        dateOut: newDateOut,
        errors: {
          ...prev.errors,
          dateDue: dateDueError,
        },
      };
    });
  };

  const setDateDue = (newDateDue: Date) => {
    setAgreementModalState((prev) => {
      let dateDueError: { id: string } | null;

      if (prev.dateOut && newDateDue < prev.dateOut) {
        // If setting dateDue earlier than dateOut
        dateDueError = { id: 'errors.validation.dateDueBeforeDateOut' };
      } else if (prev.dateOut && newDateDue > prev.dateOut) {
        // If setting dateDue later than dateOut
        dateDueError = null;
      }

      return {
        ...prev,
        dateDue: newDateDue,
        errors: {
          ...prev.errors,
          dateDue: dateDueError,
        },
      };
    });
  };

  const isExistingCustomer = 'id' in customer && customer.id !== null;

  const handleCreateAgreement = (
    { redirect, status }: ButtonOptions = {
      redirect: true,
      status: AgreementStatus.BOOKED,
    },
    successCallback: (agreementId?: number) => void
  ) => {
    const modalStateJson = isExistingCustomer
      ? {
          // Preseleted Customer
          customerSource: customer?.id,
          customerFirstName: customer?.firstName,
          customerLastName: customer?.lastName,
          customerEmail: customer?.email,
          customerPhoneCell: customer?.phoneCell,
          customerPhoneResidential: customer?.phoneResidential,
          customerPhoneBusiness: customer?.phoneBusiness,
          dateOut: dateOut?.toISOString(),
          dateDue: dateDue?.toISOString(),
        }
      : {
          // New Customer
          customerFirstName: customer?.firstName,
          customerLastName: customer?.lastName,
          customerPhoneCell: customer?.phoneCell,
          dateOut: dateOut?.toISOString(),
          dateDue: dateDue?.toISOString(),
        };

    const agreementJson = {
      vehicleSource: vehicle?.id,
      dealership: user?.profile.dealership.id,
      vehicleOdometerUnit: vehicle?.odometerUnit || dealershipSettings.get('dealershipOdometerUnit'),
      vehicleFuelUnit: vehicle?.fuelUnit || dealershipSettings.get('dealershipFuelUnit'),
      status,
      ...modalStateJson,
    };

    // if the vehicle or customer belongs to a different dealership within the user's hierarchy, then we "act" as that dealership when creating an agreement
    const targetDealership: number | null = displayDealershipSelector
      ? selectedDealership
      : vehicle?.dealership ?? ('dealership' in customer ? customer.dealership : null);
    const actingAs: number | null =
      targetDealership && targetDealership !== user?.profile.dealership.id ? targetDealership : null;

    return dispatch(createAgreement(agreementJson, actingAs))
      .then(async (response: Agreement) => {
        const { id: agreementId } = response;
        if (typeof successCallback === 'function') {
          await successCallback(agreementId);
        }

        if (redirect) {
          historyPush(history, `/fleet/agreements/${agreementId}`);
        }

        onClose();
      })
      .catch((error) => {
        setAgreementModalState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            // @ts-expect-error TS(2786): 'ApiErrors' cannot be used as a JSX component.
            createAgreement: <ApiErrors errors={error.data} />,
          },
        }));

        setIsFlashMessageVisible(true);
      });
  };

  const customerCardRef = useRef(null);

  // Auto-focus the customer search field when modal opens or when customer is cleared
  useEffect(() => {
    if ((isOpen && customerCardRef.current && !customerReadOnly) || (isOpen && customer === emptyCustomer)) {
      customerCardRef.current?.focus();
    }
  }, [isOpen, customerCardRef, customerReadOnly, customer]);

  const sendMixpanelEvent = () => {
    if (!isExistingCustomer) {
      mixpanel.track(ANALYTICS_EVENTS.CREATE_NEW_CUSTOMER, { action: ANALYTICS_EVENTS.CREATE_AGREEMENT });
    }
  };

  return (
    <StyledModal onClose={onClose} onOpen={onOpen} open={isOpen} trigger={<div />} data-testid="AgreementModal">
      <StyledModalHeader>{formatMessage({ id: 'agreementModal.title' })}</StyledModalHeader>
      <StyledModalContent>
        {description && (
          <Modal.Description>
            <StyledDescription>{description}</StyledDescription>
          </Modal.Description>
        )}
        <FlashMessage
          visible={isFlashMessageVisible}
          type={FLASH_MESSAGE_TYPES.error}
          onClose={() => setIsFlashMessageVisible(false)}
          text={errorMessage}
        />
        {displayDealershipSelector && (
          <DealershipModalCard
            hierarchy={mappedHierarchy}
            selectedDealership={selectedDealership}
            onSelect={setSelectedDealership}
          />
        )}
        {vehicle && <VehicleModalCard vehicle={vehicle} />}
        {(!displayDealershipSelector || selectedDealership) && (
          <CustomerModalCard
            ref={customerCardRef}
            customer={customer}
            preventModification={customerReadOnly}
            setCustomer={setCustomer}
            errors={errors}
            setErrors={setErrors}
            dealershipId={displayDealershipSelector ? selectedDealership : vehicle?.dealership}
          />
        )}
        <RentalDetailsModalCard
          dateDue={displayedDateDue}
          dateOut={displayedDateOut}
          errors={errors}
          setDateOut={setDateOut}
          setDateDue={setDateDue}
        />
      </StyledModalContent>
      <StyledModalActions>
        <StyledCancelButton onClick={onClose} />
        <SuccessButton
          onClick={() => {
            handleCreateAgreement(successButtonClickOptions, (id) => {
              if (typeof onSuccessCallback === 'function') {
                onSuccessCallback(id);
              }
              onClose();
            });
            sendMixpanelEvent();
          }}
          disabled={!requiredFields || anyError}
        >
          {successButtonLabel}
        </SuccessButton>
        <PrimaryButton
          loading={creationLoading}
          onClick={async () => {
            setCreationLoading(true);
            await handleCreateAgreement(primaryButtonClickOptions, (id) => {
              if (typeof onPrimaryCallback === 'function') {
                onPrimaryCallback(id);
              }
              onClose();
            });
            setCreationLoading(false);
            sendMixpanelEvent();
          }}
          disabled={!requiredFields || anyError}
        >
          {primaryButtonLabel}
        </PrimaryButton>
      </StyledModalActions>
    </StyledModal>
  );
};

export default AgreementModal;
